import { useEffect, useState } from "react";
import LandingPadsplit from "@/pages/landing-padsplit";
import HomeV2 from "@/pages/home-v2";
import HomeV3 from "@/pages/home-v3";
import { useSEO } from "@/hooks/use-seo";
import { buildHomeV2JsonLd, seoFor } from "@/data/marketing-routes";
import {
  HOMEPAGE_VERSION_ENDPOINT,
  injectedHomepageVersion,
  isHomepageVersion,
  type HomepageVersion,
} from "@/lib/marketing/homepage-version";

/** Resolves the homepage version: the server's injected value, else the public endpoint (dev). */
export function useHomepageVersion(): HomepageVersion | null {
  const [version, setVersion] = useState<HomepageVersion | null>(() => injectedHomepageVersion());

  useEffect(() => {
    if (version) return;
    let cancelled = false;
    fetch(HOMEPAGE_VERSION_ENDPOINT, { credentials: "include" })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { version?: unknown } | null) => {
        const next = data?.version;
        if (!cancelled) setVersion(isHomepageVersion(next) ? next : "v1");
      })
      .catch(() => {
        if (!cancelled) setVersion("v1");
      });
    return () => {
      cancelled = true;
    };
  }, [version]);

  return version;
}

/** Homepage v2 with its SEO: canonical at `/` when live; `/v2` is always noindex. */
export function HomeV2Route({ at }: { at: "/" | "/v2" }) {
  useSEO(at === "/" ? { ...seoFor("/"), jsonLd: buildHomeV2JsonLd() } : seoFor("/v2"));
  return <HomeV2 />;
}

/** `/`: Homepage v1 (unchanged) by default; v2 once the switch is flipped (build F4). */
export default function HomePage() {
  const version = useHomepageVersion();
  if (version === null) return <div className="min-h-screen bg-[#0a1628]" data-testid="home-loading" />;
  return version === "v2" ? <HomeV2Route at="/" /> : <LandingPadsplit />;
}

export function HomeV2Preview() {
  return <HomeV2Route at="/v2" />;
}

/** V3 is review-only; it is not a new root-version setting. */
export function HomeV3Preview() {
  useSEO(seoFor("/v3"));
  return <HomeV3 />;
}
